import React, { useCallback, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import {
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
} from '../types';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  MAX_STRINGS_PER_GROUP,
  SOURCE_TEXT_CHAR_LIMIT,
  STRING_ID_FORMAT_ERROR,
  STRING_ID_MAX_LENGTH,
  STRING_ID_MAX_LENGTH_ERROR,
  STRING_ID_PATTERN,
} from '../constants';
import styles from '../StringGroupDetail.module.scss';

interface StringEntry {
  stringId: string;
  sourceText: string;
  contextNote: string;
  inlineMarkupType: InlineMarkupType;
  messageFormatType: MessageFormatType;
}

interface StringEntryErrors {
  stringId?: string;
  sourceText?: string;
  contextNote?: string;
}

interface CreateStringModalProps {
  visible: boolean;
  sourceLocale: string;
  existingStringIds: string[];
  draftAddedStringIds: string[];
  onSubmit: (strings: DraftStringInput[]) => void;
  onDismiss: () => void;
  onSuccess: (count: number) => void;
}

const EMPTY_ENTRY: StringEntry = {
  stringId: '',
  sourceText: '',
  contextNote: '',
  inlineMarkupType: InlineMarkupType.PlainText,
  messageFormatType: MessageFormatType.None,
};

const CreateStringModal: React.FC<CreateStringModalProps> = ({
  visible,
  sourceLocale,
  existingStringIds,
  draftAddedStringIds,
  onSubmit,
  onDismiss,
  onSuccess,
}) => {
  const [entries, setEntries] = useState<StringEntry[]>([{ ...EMPTY_ENTRY }]);
  const [errors, setErrors] = useState<StringEntryErrors[]>([{}]);
  const [limitError, setLimitError] = useState<string | null>(null);

  // Strings already in the group (server + draft) count against the per-group
  // cap, so the form can only add the remaining headroom.
  const existingTotal = existingStringIds.length + draftAddedStringIds.length;
  const remainingSlots = Math.max(MAX_STRINGS_PER_GROUP - existingTotal, 0);

  const resetForm = useCallback(() => {
    setEntries([{ ...EMPTY_ENTRY }]);
    setErrors([{}]);
    setLimitError(null);
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
  }, [onDismiss, resetForm]);

  const updateEntry = (
    index: number,
    field: keyof StringEntry,
    value: string,
  ) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // Clear error for the field being edited
    setErrors((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: undefined };
      return updated;
    });
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, { ...EMPTY_ENTRY }]);
    setErrors((prev) => [...prev, {}]);
  };

  const removeEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const newErrors: StringEntryErrors[] = entries.map((entry, index) => {
      const entryErrors: StringEntryErrors = {};
      // Validate the trimmed values because that is what gets submitted, so a
      // value that passes here cannot be rejected (or silently truncated)
      // later.
      const stringId = entry.stringId.trim();
      const sourceText = entry.sourceText.trim();
      const contextNote = entry.contextNote.trim();

      if (!stringId) {
        entryErrors.stringId = 'String ID is required.';
      } else {
        // Check format against backend pattern
        if (!STRING_ID_PATTERN.test(stringId)) {
          entryErrors.stringId = STRING_ID_FORMAT_ERROR;
        } else if (stringId.length > STRING_ID_MAX_LENGTH) {
          entryErrors.stringId = STRING_ID_MAX_LENGTH_ERROR;
        } else {
          // Check uniqueness against existing strings and other draft additions
          const allExistingIds = [...existingStringIds, ...draftAddedStringIds];
          if (allExistingIds.includes(stringId)) {
            entryErrors.stringId = 'A string with this ID already exists.';
          } else {
            // Check uniqueness within this batch
            const otherIds = entries
              .filter((_, i) => i !== index)
              .map((e) => e.stringId.trim());
            if (otherIds.includes(stringId)) {
              entryErrors.stringId = 'Duplicate String ID within this batch.';
            }
          }
        }
      }

      if (!sourceText) {
        entryErrors.sourceText = 'Source text is required.';
      } else if (sourceText.length > SOURCE_TEXT_CHAR_LIMIT) {
        entryErrors.sourceText = `Source text exceeds ${SOURCE_TEXT_CHAR_LIMIT} character limit.`;
      }

      if (!contextNote) {
        entryErrors.contextNote = 'Context note is required.';
      } else if (contextNote.length > CONTEXT_NOTE_CHAR_LIMIT) {
        entryErrors.contextNote = `Context note exceeds ${CONTEXT_NOTE_CHAR_LIMIT} character limit.`;
      }

      return entryErrors;
    });

    setErrors(newErrors);

    // Enforce the cap the strings table advertises, so the user gets a form
    // error here instead of a backend rejection after submitting.
    const totalAfterSubmit = existingTotal + entries.length;
    const overLimit = totalAfterSubmit > MAX_STRINGS_PER_GROUP;
    setLimitError(
      overLimit
        ? `A string group can hold at most ${MAX_STRINGS_PER_GROUP} strings. This group has ${existingTotal}, so you can add ${remainingSlots} more.`
        : null,
    );

    return (
      !overLimit &&
      newErrors.every((e) => !e.stringId && !e.sourceText && !e.contextNote)
    );
  };

  const handleSubmitAll = () => {
    if (!validate()) {
      return;
    }

    const drafts: DraftStringInput[] = entries.map((entry) => ({
      stringId: entry.stringId.trim(),
      sourceLocale,
      // Trimmed to match what validate() checked, so surrounding whitespace is
      // never persisted into the draft.
      sourceText: entry.sourceText.trim(),
      contextNote: entry.contextNote.trim(),
      inlineMarkupType: entry.inlineMarkupType,
      messageFormatType: entry.messageFormatType,
      variants: [],
    }));

    // Add each string to draft state
    for (const draft of drafts) {
      onSubmit([draft]);
    }

    const count = drafts.length;
    resetForm();
    onDismiss();
    onSuccess(count);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Create Strings"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmitAll}>
              Submit All
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {limitError && <Alert type="error">{limitError}</Alert>}

        {entries.map((entry, index) => (
          <div key={index} className={styles.stringEntryCard}>
            <div className={styles.stringEntryHeader}>
              <Box variant="h4">String {index + 1}</Box>
              {entries.length > 1 && (
                <Button variant="link" onClick={() => removeEntry(index)}>
                  Remove
                </Button>
              )}
            </div>

            <ColumnLayout columns={2}>
              <FormField
                label="String ID *"
                errorText={errors[index]?.stringId}
              >
                <Input
                  value={entry.stringId}
                  placeholder="BUTTON_LABEL, CATEGORY_TITLE"
                  onChange={({ detail }) =>
                    updateEntry(index, 'stringId', detail.value)
                  }
                />
              </FormField>
              <FormField
                label="Source locale"
                constraintText="Fixed for this group"
              >
                <Input value={sourceLocale} disabled />
              </FormField>
            </ColumnLayout>

            <FormField
              label="Source text *"
              constraintText={`Character limit: ${SOURCE_TEXT_CHAR_LIMIT.toLocaleString()}`}
              errorText={errors[index]?.sourceText}
            >
              <Textarea
                value={entry.sourceText}
                placeholder="Enter source text."
                onChange={({ detail }) =>
                  updateEntry(index, 'sourceText', detail.value)
                }
              />
            </FormField>

            <FormField
              label="Context note *"
              errorText={errors[index]?.contextNote}
              constraintText={`Character limit: ${CONTEXT_NOTE_CHAR_LIMIT.toLocaleString()}`}
            >
              <Textarea
                value={entry.contextNote}
                placeholder="Provide context for translators."
                rows={2}
                onChange={({ detail }) =>
                  updateEntry(index, 'contextNote', detail.value)
                }
              />
            </FormField>

            <FormField label="Inline Markup *">
              <RadioGroup
                value={entry.inlineMarkupType}
                items={[
                  { value: InlineMarkupType.PlainText, label: 'Plain Text' },
                  { value: InlineMarkupType.Html, label: 'HTML' },
                  { value: InlineMarkupType.Markdown, label: 'Markdown' },
                ]}
                onChange={({ detail }) =>
                  updateEntry(
                    index,
                    'inlineMarkupType',
                    detail.value as InlineMarkupType,
                  )
                }
              />
            </FormField>

            <FormField label="Message Format *">
              <RadioGroup
                value={entry.messageFormatType}
                items={[
                  { value: MessageFormatType.None, label: 'None' },
                  { value: MessageFormatType.HashGet, label: 'Hash-Get' },
                  {
                    value: MessageFormatType.AmazonMessageFormat,
                    label: 'Amazon Message Format',
                  },
                  { value: MessageFormatType.Mustache, label: 'Mustache' },
                ]}
                onChange={({ detail }) =>
                  updateEntry(
                    index,
                    'messageFormatType',
                    detail.value as MessageFormatType,
                  )
                }
              />
            </FormField>
          </div>
        ))}

        <Button
          variant="normal"
          iconName="add-plus"
          onClick={addEntry}
          disabled={entries.length >= remainingSlots}
          fullWidth
        >
          Add Another String
        </Button>

        <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
          {entries.length} string(s) ready to submit
        </Box>
      </SpaceBetween>
    </Modal>
  );
};

export default CreateStringModal;
